import { useEffect, useState } from 'react';
import { upload } from 'src/helpers/upLoadHelper';
import { getTicketStatus, getModulePermissions } from 'src/services/client';
import { toast } from 'react-toastify';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import {
  useServiceRequestAPI,
  UseServiceRequestAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestAPI';
import {
  useMessagesAPI,
  UseMessagesAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useMessagesAPI';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import { buildOfflineUser, decodeTokenPayload, OfflineUser } from '../helpers';

interface ModulePermission {
  id: string;
  name: string;
  description: string;
}

interface ModulePermissionsResponse {
  statusCode: number;
  message: string;
  data: ModulePermission[];
}

interface UseOfflineServiceRequestAPIState {
  statuses: any[];
  saving: boolean;
  homeOwnerDataVisible: boolean | null;
}

export interface UseMagicLinkServiceRequestAPIReturn {
  serviceRequest: any;
  activity: any[];
  loading: boolean;
  error: string | null;
  oneYearWarrantyIssues: any;
  documents: any[];
  loadingDocs: boolean;
  messages: any[];
  loadingMessages: boolean;
  isSending: boolean;

  statuses: any[];
  saving: boolean;
  homeOwnerDataVisible: boolean | null;
  selectedStatusId: string;

  fetchServiceRequestById: (id: string) => Promise<any>;
  fetchDocuments: (requestId: string, token?: string) => Promise<void>;
  refreshData: () => Promise<void>;
  updateStatus: (newStatusId: string) => Promise<void>;
  saveDocument: (
    document: DocumentFormData,
    serviceRequestId: string,
    passedUserId: string
  ) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  postMessageToAPI: (chat: any) => Promise<void>;

  saveScheduledAppointmentFromAPI: () => Promise<void>;
  updateServiceRequestOneYearWarrantyIssueStatusFromAPI: () => Promise<void>;
  deleteServiceRequestFromAPI: () => Promise<boolean>;
}

export const useMagicLinkServiceRequestAPI = (
  serviceRequestId: string,
  token: string,
  userId: string
): UseMagicLinkServiceRequestAPIReturn => {
  const tokenPayload: any = decodeTokenPayload(token);
  const firstName: string = tokenPayload?.firstName ?? '';
  const lastName: string = tokenPayload?.lastName ?? '';
  const offlineUser: OfflineUser = buildOfflineUser(
    userId,
    firstName,
    lastName
  );

  const srAPI: UseServiceRequestAPIReturn = useServiceRequestAPI(token);
  const messagesAPI: UseMessagesAPIReturn = useMessagesAPI(token);
  const documentsAPI: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI({
    itemData: 'Ticket',
    user: offlineUser,
    token: token,
    customUploadErrorMessage:
      'Failed to upload this document. Please try again.',
  } as any);

  const [state, setState] = useState<UseOfflineServiceRequestAPIState>({
    statuses: [],
    saving: false,
    homeOwnerDataVisible: null,
  });

  const setSaving = (saving: boolean) => {
    setState((prev: UseOfflineServiceRequestAPIState) => ({ ...prev, saving }));
  };

  const refreshData = async (): Promise<void> => {
    await Promise.all([
      documentsAPI.fetchServiceRequestDocuments(serviceRequestId, token),
      srAPI.fetchServiceRequestActivity(serviceRequestId),
    ]);

    const fetchedSR: any =
      await srAPI.fetchServiceRequestById(serviceRequestId);
    const propertyId: string = fetchedSR?.suite?.property?.id ?? '';

    try {
      const statusResponse: any = await getTicketStatus(token);

      const statusData: any[] =
        statusResponse?.status === 200 || statusResponse?.ok
          ? await (statusResponse as Response).json()
          : [];

      await messagesAPI.fetchMessagesWithToken(serviceRequestId);

      let homeOwnerDataVisible: boolean = false;

      if (propertyId) {
        const modulePermissionsResponse: any = await getModulePermissions(
          propertyId,
          token
        );

        if (modulePermissionsResponse?.ok) {
          const { data: permissions }: ModulePermissionsResponse = await (
            modulePermissionsResponse as Response
          ).json();

          homeOwnerDataVisible = permissions.some(
            (p: ModulePermission) => p.name === 'Home Owner Data Visibility'
          );
        }
      }

      setState((prev: UseOfflineServiceRequestAPIState) => ({
        ...prev,
        statuses: statusData ?? [],
        homeOwnerDataVisible,
      }));
    } catch (error) {
      console.error('Error fetching offline service request:', error);
    }
  };

  const updateStatus = async (newStatusId: string): Promise<void> => {
    const status: any = state.statuses.find((s: any) => s.id === newStatusId);

    if (!status) return;

    setSaving(true);

    try {
      const success: any = await srAPI.updateStatus(
        { name: status.name, TicketStatusId: status.id, LoginUserId: userId },
        serviceRequestId
      );

      if (!success) {
        toast.error('🚫 Failed to update status, please try again.', {
          autoClose: 12000,
          position: 'top-center',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDocument = async (
    document: DocumentFormData,
    serviceRequestId: string,
    passedUserId: string
  ): Promise<void> => {
    setSaving(true);
    try {
      await documentsAPI.saveDocument(document, serviceRequestId, passedUserId);
    } catch (error) {
      console.error('Error adding document:', error);
    } finally {
      setSaving(false);
    }
  };

  const postMessageToAPI = async (chat: any): Promise<void> => {
    setSaving(true);

    try {
      let imageUrl: string | null = null;
      let finalMessage = chat.Message || chat.message;
      let imagesArray = chat.image || chat.images;

      if (imagesArray?.length) {
        imageUrl = (await upload(imagesArray[0], token)) ?? null;
      }

      const propertyManagers: any =
        srAPI.serviceRequest?.suite?.property?.propertyManagers ?? [];
      const recipientId: string = propertyManagers[0]?.id ?? '';

      if (!recipientId) {
        toast.error('No property manager assigned. Unable to send message.');
        return;
      }

      const extractFileName = (url: string): string => {
        const parts = url.split('.com/');
        return parts.length > 1 ? parts[1] : '';
      };

      const messageData = {
        SentBy: userId,
        SentTo: recipientId,
        ReferenceId: serviceRequestId,
        ReferenceType: 'Ticket',
        Message: finalMessage,
        FileName: imageUrl ? extractFileName(imageUrl) : '',
        FileURL: imageUrl ?? '',
        TicketNumber: srAPI.serviceRequest?.ticketId?.toString() ?? '',
        ActionUrl: `app/service-requests/${serviceRequestId}/activeTab=1`,
      };

      await messagesAPI.postMessageToAPI(messageData);
      await messagesAPI.fetchMessagesWithToken(serviceRequestId);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteDocument = async (documentId: string): Promise<void> => {
    setSaving(true);
    try {
      await documentsAPI.deleteDocument(documentId);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    refreshData();
  }, []);

  return {
    serviceRequest: srAPI.serviceRequest,
    activity: srAPI.activity,
    loading: srAPI.loading,
    error: srAPI.error,
    oneYearWarrantyIssues: srAPI.oneYearWarrantyIssues,

    documents: documentsAPI.documents,
    loadingDocs: documentsAPI.loadingDocs,

    messages: messagesAPI.messages,
    loadingMessages: messagesAPI.loadingMessages,
    isSending: messagesAPI.isSending || state.saving,

    ...state,
    selectedStatusId: srAPI.serviceRequest?.status?.id ?? '',

    fetchServiceRequestById: srAPI.fetchServiceRequestById,
    fetchDocuments: documentsAPI.fetchServiceRequestDocuments,
    refreshData,
    updateStatus,
    saveDocument,
    deleteDocument,
    postMessageToAPI,
    saveScheduledAppointmentFromAPI: async () => {},
    updateServiceRequestOneYearWarrantyIssueStatusFromAPI: async () => {},
    deleteServiceRequestFromAPI: async () => false,
  };
};
